import { useCallback, useEffect, useRef, useState } from 'react';
import { clampWidthPct, snapWidthPct } from './chart-size';
import type { MouseEvent as ReactMouseEvent, MutableRefObject, RefObject } from 'react';

type Side = 'left' | 'right';

type DragState = {
    side: Side;
    startX: number;
    startWidth: number;
    columnWidth: number;
};

type UseChartResizeProps = {
    disabled: boolean;
    containerRef: RefObject<HTMLDivElement | null>;
    storedPct?: number;
    /** Intrinsic canvas aspect (width / height), for deriving the effective
     *  width of a legacy contain-fit box on the first grab. */
    aspect: number;
    onCommit: (pct: number) => void;
};

type UseChartResizeResult = {
    /** Width % while a drag is in flight; null when idle. */
    livePct: number | null;
    isResizing: boolean;
    handleResizeStart: (side: Side, event: ReactMouseEvent) => void;
    /** Set when a drag actually moved; consume it to suppress the click that
     *  follows mouseup so it never opens the config modal. */
    didDragRef: MutableRefObject<boolean>;
};

export const useChartResize = ({
    disabled,
    containerRef,
    storedPct,
    aspect,
    onCommit,
}: UseChartResizeProps): UseChartResizeResult => {
    const [drag, setDrag] = useState<DragState | null>(null);
    const [livePct, setLivePct] = useState<number | null>(null);
    const livePctRef = useRef<number | null>(null);
    const didDragRef = useRef(false);

    const handleResizeStart = useCallback(
        (side: Side, event: ReactMouseEvent) => {
            if (disabled) return;
            const container = containerRef.current;
            const column = container?.parentElement;
            if (!container || !column) return;
            event.preventDefault();
            event.stopPropagation();
            const columnWidth = column.getBoundingClientRect().width;
            if (columnWidth <= 0) return;
            const rect = container.getBoundingClientRect();
            // Legacy boxes are full-width with a contain-fit chart: start from
            // the width the chart visually occupies so the grab doesn't jump.
            const startWidth =
                storedPct !== undefined
                    ? (columnWidth * storedPct) / 100
                    : Math.min(rect.width, rect.height * aspect);
            setDrag({ side, startX: event.clientX, startWidth, columnWidth });
            setLivePct(clampWidthPct((startWidth / columnWidth) * 100));
        },
        [aspect, containerRef, disabled, storedPct],
    );

    useEffect(() => {
        livePctRef.current = livePct;
    }, [livePct]);

    useEffect(() => {
        if (!drag) return;

        const handleMouseMove = (event: MouseEvent) => {
            const delta = event.clientX - drag.startX;
            if (Math.abs(delta) > 2) didDragRef.current = true;
            // The box stays centered, so each edge moves half the total change.
            const signed = drag.side === 'right' ? delta : -delta;
            const nextWidth = drag.startWidth + signed * 2;
            setLivePct(snapWidthPct(clampWidthPct((nextWidth / drag.columnWidth) * 100)));
        };

        const handleMouseUp = () => {
            const pct = livePctRef.current;
            setDrag(null);
            setLivePct(null);
            if (pct !== null) onCommit(Math.round(pct));
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setDrag(null);
            setLivePct(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [drag, onCommit]);

    return { livePct, isResizing: drag !== null, handleResizeStart, didDragRef };
};
